import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { NotebookPen, Plus, Trash2, ArrowRight, FileCode2, Sparkles, GitBranch } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function KnuthDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav("/login");
      return;
    }
    api.get("/knuth/projects")
      .then(({ data }) => setProjects(data.projects || []))
      .catch(() => setProjects([]))
      .finally(() => setLoadingList(false));
  }, [user, loading, nav]);

  const remove = async (id) => {
    if (!window.confirm("Delete this project and all generated docs?")) return;
    try {
      await api.delete(`/knuth/projects/${id}`);
      setProjects(projects.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const totalFiles = projects.reduce((s, p) => s + (p.understanding?.file_count || 0), 0);
  const totalDocs = projects.reduce((s, p) => s + (p.docs?.length || 0), 0);

  return (
    <div className="container-narrow py-10 md:py-14" data-testid="knuth-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow mb-2 inline-flex items-center gap-2">
            <NotebookPen className="w-3 h-3" /> Knuth workbench
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold">Your projects</h1>
          <p className="text-ink2 mt-2 text-sm">Give Knuth software. It explains it, documents it, diagrams it.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/apps/knuth" className="btn-ghost text-sm" data-testid="knuth-about-link">About Knuth</Link>
          <Link to="/knuth/new" className="btn-primary" data-testid="knuth-new-btn">
            <Plus className="w-4 h-4" /> New project
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-8" data-testid="knuth-stats">
        <Stat label="Projects" value={projects.length} testid="stat-projects" />
        <Stat label="Files indexed" value={totalFiles.toLocaleString()} testid="stat-files" />
        <Stat label="Docs generated" value={totalDocs} testid="stat-docs" />
        <Stat label="Repositories" value={projects.filter((p) => p.source === "git").length} testid="stat-repos" />
      </div>

      {/* Projects list */}
      {loadingList ? (
        <div className="card-soft p-10 text-center text-ink2 text-sm">Loading projects…</div>
      ) : projects.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3" data-testid="knuth-projects-list">
          {projects.map((p) => (
            <div key={p.id} className="card-soft" data-testid={`knuth-project-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 p-5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-xl font-semibold truncate">{p.name}</span>
                    <StatusBadge status={p.status} />
                    {p.understanding?.primary_language && (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-ink2 border border-border rounded-full px-2 py-0.5">
                        {p.understanding.primary_language}
                      </span>
                    )}
                  </div>
                  {p.description && <div className="text-sm text-ink2 mt-0.5 truncate">{p.description}</div>}
                  <div className="text-xs text-ink2 mt-1.5 inline-flex items-center gap-3 flex-wrap">
                    {p.understanding?.file_count > 0 && (
                      <span className="inline-flex items-center gap-1"><FileCode2 className="w-3 h-3" /> {p.understanding.file_count} files</span>
                    )}
                    {p.understanding?.top_modules?.length > 0 && (
                      <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-marigold" /> {p.understanding.top_modules.slice(0, 3).join(" · ")}</span>
                    )}
                    <span>Updated {new Date(p.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Link
                    to={`/knuth/projects/${p.id}`}
                    className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition"
                    data-testid={`knuth-open-${p.id}`}
                  >
                    Open <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <button
                    onClick={() => remove(p.id)}
                    className="text-marigold hover:text-marigold/80 p-2"
                    data-testid={`knuth-delete-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="font-heading text-3xl font-semibold">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    ready:     { label: "Ready",      cls: "bg-teal/15 text-teal" },
    analyzing: { label: "Analyzing…", cls: "bg-marigold/15 text-marigold" },
    empty:     { label: "Empty",      cls: "bg-paper text-ink2" },
    failed:    { label: "Failed",     cls: "bg-red-100 text-red-600" },
  }[status] || { label: status || "unknown", cls: "bg-paper text-ink2" };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="card-soft p-10 md:p-14 text-center" data-testid="knuth-empty">
      <div className="w-16 h-16 rounded-2xl bg-teal/15 text-teal flex items-center justify-center mx-auto mb-4">
        <NotebookPen className="w-8 h-8" />
      </div>
      <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-2">No projects yet.</h2>
      <p className="text-ink2 max-w-md mx-auto mb-6">Drop a ZIP of any codebase or connect a Git repo. Knuth walks the tree, indexes the files, and gets ready to explain everything.</p>
      <div className="flex flex-wrap justify-center gap-3">
        <Link to="/knuth/new" className="btn-primary" data-testid="knuth-empty-cta">
          <Plus className="w-4 h-4" /> Create your first project
        </Link>
        <span className="inline-flex items-center text-xs text-ink2 gap-1.5">
          <GitBranch className="w-3.5 h-3.5" /> Git integration coming next
        </span>
      </div>
    </div>
  );
}
